// Top navigation bar. Hosts the primary page links, the command-palette
// trigger, search, nav-lock toggle, comparator pill, feature settings
// gear and customise menus. Most controls are pass-through to the shell.
//
// Postgres mapping: none.
// Integration hook: permissions checks for admin-only items
// (FeatureSettings) should come from the existing auth slice.
import React, { useRef, useState, useEffect } from 'react';
import { Menu, MenuItem, ListItemText, Tooltip, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import useFeature from '../useFeature';

// Replace the harsh browser-default focus ring with a subtle teal halo that
// matches the rest of the brand. `:focus` (mouse click) is suppressed; only
// `:focus-visible` (keyboard navigation) shows the ring so click users
// don't get a stuck halo on the active chip.
const NAV_CSS = `
.rbx-nav-chip:focus { outline: none; box-shadow: none; }
.rbx-nav-chip:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0,191,165,0.35);
}
`;

// Top-of-page primary nav. Pill chip per destination, active state is a
// filled chip (the MUI primary palette colour, with auto-contrast text);
// inactive is transparent with a hover surface. Export destinations
// (builder / scheduled / data tables
// / history) collapse under a single "Exporting v" chip because the portal
// itself is the reports section, surfacing them at the top level would be
// redundantly recursive.
//
// "Home" is renamed to "Risk" because every other page is a child of the
// risk story: the landing surface *is* the risk overview, and naming it so
// gives the rail an honest entry point.
// Labels stored in sentence case so they read naturally when they're
// reused as breadcrumb crumbs further down the navigation stack. The
// nav chips themselves render uppercase via CSS textTransform on the
// button style, so the rail still looks like the brand chips even though
// the source string is "Risk", not "RISK".
const NAV = [
    { id: 'summary',         label: 'Risk'         },
    { id: 'sims',            label: 'Simulations'  },
    { id: 'courses',         label: 'Courses'      },
    { id: 'users',           label: 'Users'        },
    { id: 'dwm',             label: 'Dark web',      feature: 'darkWebMonitoring' },
];

const EXPORTING_MENU = [
    { id: 'export-builder',   label: 'Export builder',    description: 'Compose a custom export and download it.',  feature: 'exportBuilder' },
    { id: 'export-scheduled', label: 'Scheduled exports', description: 'Automated monthly deliveries.',             feature: 'exportBuilder' },
    { id: 'export-tables',    label: 'Data tables',       description: 'Download full data tables on their own.',   feature: 'exportBuilder' },
    { id: 'export-history',   label: 'Export history',    description: 'Everything you have pulled or sent.',       feature: 'exportBuilder' },
];

const OUTPUT_PAGE_IDS = new Set(EXPORTING_MENU.map((m) => m.id));

export default function NavBar({ currentPage, go, openCommandPalette, locked, onToggleLock }) {
    const flags = {
        flaggedSimulations: useFeature('flaggedSimulations'),
        darkWebMonitoring:  useFeature('darkWebMonitoring'),
        exportBuilder:      useFeature('exportBuilder'),
    };
    const items = NAV.filter((it) => !it.feature || flags[it.feature]);
    const outputItems = EXPORTING_MENU.filter((it) => !it.feature || flags[it.feature]);

    const [outputsAnchor, setOutputsAnchor] = useState(null);

    // The active tab is the top-level page the breadcrumb belongs to. Drill
    // detail pages inherit the active state of their origin so the nav
    // doesn't appear to deselect when the user drills in.
    const activeId = (() => {
        if (currentPage === 'sim-detail' || currentPage === 'send-history' || currentPage === 'all-sims-table') return 'sims';
        if (currentPage === 'course-send-log' || currentPage === 'outstanding-users' || currentPage === 'all-courses-table' || currentPage === 'course-detail') return 'courses';
        if (currentPage === 'risk-methodology' || currentPage === 'factor-detail') return 'summary';
        if (currentPage === 'user-detail' || currentPage === 'high-risk-users') return 'users';
        if (currentPage === 'meta-list' || currentPage === 'dept') return null;
        return currentPage;
    })();
    const outputsActive = OUTPUT_PAGE_IDS.has(activeId);

    // Detect Mac vs PC for the palette hint.
    const [isMac, setIsMac] = useState(false);
    useEffect(() => {
        try { setIsMac(/Mac|iPhone|iPad/.test(navigator.platform)); } catch { /* SSR */ }
    }, []);
    const paletteHint = isMac ? 'CmdK' : 'Ctrl K';

    return (
        <nav
            aria-label="Primary"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 20px',
                background: 'transparent',
            }}
        >
            <style>{NAV_CSS}</style>
            {/* Scrollable chip group. Splitting it from the right-pinned
                CmdK trigger means the palette stays visible at all widths
                while excess chips simply scroll horizontally inside their
                own track. */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flex: 1,
                minWidth: 0,
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }}>
            {items.map((item) => (
                <NavChip
                    key={item.id}
                    label={item.label}
                    active={item.id === activeId}
                    onClick={() => go(item.id, item.label)}
                />
            ))}

            {outputItems.length > 0 && (
                <>
                    <NavChip
                        label="Exporting"
                        active={outputsActive}
                        chevron
                        chevronOpen={Boolean(outputsAnchor)}
                        onClick={(e) => setOutputsAnchor(e.currentTarget)}
                        ariaProps={{ 'aria-haspopup': 'menu', 'aria-expanded': Boolean(outputsAnchor) }}
                    />
                    <Menu
                        anchorEl={outputsAnchor}
                        open={Boolean(outputsAnchor)}
                        onClose={() => setOutputsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            sx: {
                                mt: 0.75, minWidth: 240,
                                border: '1px solid', borderColor: 'divider',
                                borderRadius: '10px', boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                            },
                        }}
                        MenuListProps={{ sx: { py: 0.5 } }}
                    >
                        {outputItems.map((item) => {
                            const active = item.id === activeId;
                            return (
                                <MenuItem
                                    key={item.id}
                                    onClick={() => { setOutputsAnchor(null); go(item.id, item.label); }}
                                    sx={{
                                        py: 1, px: 1.5, mx: 0.5,
                                        borderRadius: '6px',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        ...(active && { bgcolor: 'action.selected' }),
                                    }}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        secondary={item.description}
                                        primaryTypographyProps={{ sx: { fontSize: 12, fontWeight: active ? 700 : 600, color: 'text.primary' } }}
                                        secondaryTypographyProps={{ sx: { fontSize: 10, color: 'text.secondary', mt: 0.25, lineHeight: 1.4 } }}
                                    />
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </>
            )}
            </div>

            {/* Lock toggle: pins NavBar + FilterBar to the top on scroll. */}
            {onToggleLock && (
                <Tooltip title={locked ? 'Unpin nav and filters' : 'Pin nav and filters to top'} arrow>
                    <IconButton
                        size="small"
                        onClick={onToggleLock}
                        aria-label={locked ? 'Unpin nav' : 'Pin nav'}
                        sx={{
                            flexShrink: 0,
                            color: locked ? 'secondary.main' : 'text.disabled',
                            border: '1px solid',
                            borderColor: locked ? 'secondary.main' : 'divider',
                            borderRadius: '8px',
                            p: '5px',
                            '&:hover': { color: 'secondary.main', borderColor: 'secondary.main' },
                        }}
                    >
                        {locked ? <LockOutlinedIcon sx={{ fontSize: 14 }} /> : <LockOpenOutlinedIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                </Tooltip>
            )}

            {/* Right-pinned CmdK trigger. */}
            {openCommandPalette && (
                <button
                    type="button"
                    onClick={openCommandPalette}
                    aria-label="Open command palette"
                    style={{
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 10px',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        background: 'transparent',
                        color: 'var(--muted)',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 11,
                        cursor: 'pointer',
                        transition: 'border-color .15s, color .15s, background .15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--muted)'; e.currentTarget.style.color = 'var(--text)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--muted)'; }}
                >
                    <SearchIcon sx={{ fontSize: 14 }} />
                    Jump to...
                    <kbd style={{
                        marginLeft: 4,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: 'var(--dim)',
                        background: 'var(--surface2)',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        padding: '1px 5px',
                    }}>{paletteHint}</kbd>
                </button>
            )}
        </nav>
    );
}

function NavChip({ label, active, onClick, chevron, chevronOpen, ariaProps }) {
    const theme = useTheme();
    return (
        <button
            type="button"
            className="rbx-nav-chip"
            onClick={onClick}
            {...(ariaProps || {})}
            style={{
                border: '1px solid transparent',
                background: active ? theme.palette.primary.main : 'transparent',
                cursor: 'pointer',
                padding: chevron ? '6px 8px 6px 12px' : '6px 12px',
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: active ? theme.palette.primary.contrastText : 'var(--muted)',
                borderRadius: 8,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                flexShrink: 0,
                outline: 'none',
                transition: 'background .15s, color .15s, border-color .15s, box-shadow .15s',
            }}
            onMouseEnter={(e) => {
                if (active) return;
                e.currentTarget.style.background = 'var(--surface2)';
                e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
                if (active) return;
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--muted)';
            }}
        >
            {label}
            {chevron && (
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 16,
                        transition: 'transform 150ms',
                        transform: chevronOpen ? 'rotate(180deg)' : 'none',
                    }}
                />
            )}
        </button>
    );
}
